import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Image as ImageIcon, Loader2, ExternalLink, Sparkles, Mic, MicOff, Film, Repeat2, Play } from 'lucide-react';
import { getCurrentUser } from '../services/auth';
import { callClaude } from '../services/api';
import MediaPicker from '../components/MediaPicker';
import ImageCropModal from '../components/ImageCropModal';
import { uploadVideoToLibrary, isVideoFile } from '../lib/uploadVideoToLibrary';
import organicFetch from '../lib/organicFetch';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Voice dictation (Chrome Web Speech API). Appends to the field. Hidden if unsupported.
function MicButton({ value, onChange }) {
  const supported = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const recRef = useRef(null); const baseRef = useRef('');
  const [listening, setListening] = useState(false);
  useEffect(() => () => { try { recRef.current && recRef.current.stop(); } catch (e) { /* noop */ } }, []);
  if (!supported) return null;
  const toggle = () => {
    if (listening) { try { recRef.current && recRef.current.stop(); } catch (e) { /* noop */ } return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const r = new SR(); r.lang = 'en-US'; r.continuous = true; r.interimResults = true;
    baseRef.current = value && value.trim() ? `${value.trim()} ` : '';
    r.onresult = (e) => { let t = ''; for (let i = 0; i < e.results.length; i++) t += e.results[i][0].transcript; onChange(`${baseRef.current}${t}`); };
    r.onend = () => setListening(false); r.onerror = () => setListening(false);
    try { r.start(); recRef.current = r; setListening(true); } catch (e) { setListening(false); }
  };
  return (
    <button type="button" onClick={toggle} title="Dictate"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 14, cursor: 'pointer', fontFamily: 'Montserrat', background: listening ? '#fdecea' : '#fff', border: `1px solid ${listening ? '#e09b94' : 'var(--gray-200)'}`, color: listening ? '#c0392b' : 'var(--navy)' }}>
      {listening ? <MicOff size={12} /> : <Mic size={12} />}{listening ? 'Stop' : 'Dictate'}
    </button>
  );
}

const SITE_URL = 'https://cpx-prototype.netlify.app';
const MAX_PHOTOS = 10;
const CAPTION_MODEL = 'claude-sonnet-4-6';
const IG_SYS = `You write Instagram captions for The Crab Place (@thecrabplace), a Maryland seafood retailer. Warm, celebratory, Maryland-proud, never salesy. IMPORTANT brand voice: NO em dashes or en dashes ever (use periods, commas, parentheses); avoid the "not just X, it's Y" crutch, rule-of-three stacking, and buzzwords (elevate, indulge, crafted, curated). Instagram style: a hooky first line, a few relevant emojis, then 6 to 12 relevant hashtags at the end (e.g. #marylandcrabs #bluecrabs #crabfeast #seafood #crabplace #steamedcrabs). Do NOT put any website link in the caption (Instagram links aren't clickable; say "link in bio" only if needed). 1 to 4 short sentences plus the hashtag block. Return ONLY a JSON array of exactly 3 distinct caption strings, no other text.`;

// Public, IG-safe URLs. r2-resize downsamples so we stay under IG's 8MB image cap.
function assetUrl(a) {
  if (!a) return null;
  if (a.__externalUrl) return a.__externalUrl;   // pasted image URL (not a Library asset)
  if (a.__localUrl) return a.__localUrl;          // dropped file object URL
  const key = a.r2_original_key || a.r2_thumb_key;
  return key ? `${SITE_URL}/.netlify/functions/r2-image?key=${encodeURIComponent(key)}` : null;
}
function cropSourceUrl(a) {
  if (!a) return null;
  if (a.__externalUrl) return a.__externalUrl;
  if (a.__localUrl) return a.__localUrl;
  const key = a.r2_original_key || a.r2_thumb_key;
  return key ? `/.netlify/functions/r2-resize?key=${encodeURIComponent(key)}&edge=2400` : null;
}
function igPublicUrl(a) {
  if (!a) return null;
  if (a.__externalUrl) return a.__externalUrl;    // already a public URL IG can fetch
  const key = a.r2_original_key || a.r2_thumb_key;
  return key ? `${SITE_URL}/.netlify/functions/r2-resize?key=${encodeURIComponent(key)}&edge=1080` : null;
}
// A pasted Media Library "Copy" URL carries the R2 key as ?key=...; pull it out so
// the image flows through the exact same pipeline as a picked asset (crop, resize,
// IG-safe public URL). A non-Library URL is kept verbatim as an external image.
const VIDEO_URL_RE = /\.(mp4|mov|m4v|webm|avi)(\?|$)/i;
function assetFromUrl(raw) {
  const url = String(raw || '').trim();
  if (!url) return null;
  try {
    const u = new URL(url, SITE_URL);
    const key = u.searchParams.get('key');
    if (key) return { r2_original_key: key, suggested_name: key.split('/').pop(), __fromUrl: true };
    return { __externalUrl: url, suggested_name: (u.pathname.split('/').pop() || url), __fromUrl: true };
  } catch (e) { return null; }
}
// Downscale a dropped image to a base64 JPEG (long edge <= maxEdge) — keeps us
// under IG's 8MB cap and the media-library-upload payload small.
async function imageToBase64(file, maxEdge, quality) {
  const dataUrl = await new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = () => rej(new Error('Could not read file')); fr.readAsDataURL(file); });
  const img = await new Promise((res, rej) => { const el = new Image(); el.onload = () => res(el); el.onerror = () => rej(new Error('Could not decode image (convert HEIC/RAW to JPEG first)')); el.src = dataUrl; });
  const w = img.naturalWidth, h = img.naturalHeight;
  const scale = Math.min(1, maxEdge / Math.max(w, h));
  const cw = Math.max(1, Math.round(w * scale)), ch = Math.max(1, Math.round(h * scale));
  const canvas = document.createElement('canvas'); canvas.width = cw; canvas.height = ch;
  canvas.getContext('2d').drawImage(img, 0, 0, cw, ch);
  return { base64: canvas.toDataURL('image/jpeg', quality).split(',')[1], naturalWidth: w, naturalHeight: h };
}
// Normalize a dropped file to a size-capped JPEG File (used for the "don't save
// to Library" path so the just-in-time publish upload is always clean JPEG).
async function fileToJpegFile(file, maxEdge, quality) {
  const { base64 } = await imageToBase64(file, maxEdge, quality);
  const bin = atob(base64); const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i);
  return new File([arr], `${(file.name || 'image').replace(/\.[^.]+$/, '')}.jpg`, { type: 'image/jpeg' });
}
// HEIC/HEIF can't be canvas-decoded in the browser — read the raw bytes so the
// server can convert to JPEG (heic-convert), same as the Media Library uploader.
const isHeicFile = (f) => /image\/hei[cf]/i.test(f.type || '') || /\.(heic|heif)$/i.test(f.name || '');
async function fileToRawBase64(file) {
  const dataUrl = await new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = () => rej(new Error('Could not read file')); fr.readAsDataURL(file); });
  return String(dataUrl).slice(String(dataUrl).indexOf(',') + 1);
}
// Poster frame for a VIDEO preview. assetUrl() prefers r2_original_key (the raw
// .MOV/.mp4), which an <img> can't render — so a Reel preview showed a black box.
// Use the thumbnail (r2_thumb_key) instead; fall back to the original for the
// (rare) case a video has no thumbnail.
function videoPosterUrl(a) {
  if (!a) return null;
  const key = a.r2_thumb_key || a.r2_original_key;
  return key ? `${SITE_URL}/.netlify/functions/r2-image?key=${encodeURIComponent(key)}` : null;
}
// Playable source for a video asset. r2-fetch streams the raw original (same
// path the Media Library detail modal uses) — r2-image/r2-resize are image-only.
function videoSrcUrl(a) {
  if (!a || !a.r2_original_key) return null;
  return `/.netlify/functions/r2-fetch?key=${encodeURIComponent(a.r2_original_key)}`;
}
// Is this Media Library asset a video? Mirrors MediaPicker's detection so a photo
// slot can hold video items for a mixed carousel.
const VIDEO_EXT_RE = /\.(mov|mp4|m4v|webm|avi|mkv)$/i;
function isVideoAsset(a) {
  return !!a && (a.media_kind === 'video' || VIDEO_EXT_RE.test(String(a.r2_original_key || '')));
}
const fmtMs = (ms) => {
  const s = ms / 1000;
  const m = Math.floor(s / 60);
  return `${m}:${(s - m * 60).toFixed(1).padStart(4, '0')}`;
};

// Renders the exact video frame at `ms` — a muted video element seeked to the
// timestamp and left paused. Used for the reel cover preview: canvas capture is
// off the table because r2-fetch 302s to a presigned R2 URL (cross-origin →
// tainted canvas), but the browser will happily *display* any frame.
function SeekedFrame({ src, ms }) {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const seek = () => { try { v.currentTime = ms / 1000; } catch (e) { /* not seekable yet */ } };
    if (v.readyState >= 1) seek();
    else v.addEventListener('loadedmetadata', seek, { once: true });
    return () => v.removeEventListener('loadedmetadata', seek);
  }, [ms]);
  return <video ref={ref} src={src} muted playsInline preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }} />;
}

// Preview a picked reel video + choose its cover frame. Pause/seek to a frame,
// "Use this frame as cover" records the offset in ms — shown via SeekedFrame in
// the builder and sent to IG as thumb_offset at publish time.
function VideoPreviewModal({ src, coverMs, onSetCover, onClearCover, onClose }) {
  const vidRef = useRef(null);
  const [err, setErr] = useState(null);
  const capture = () => {
    const v = vidRef.current;
    if (!v || !Number.isFinite(v.currentTime)) return;
    onSetCover(Math.round(v.currentTime * 1000));
  };
  const modalBtn = { ...miniBtn, fontSize: 11, padding: '6px 10px' };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#1c1c1e', borderRadius: 10, padding: 14, maxWidth: 440, width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <video ref={vidRef} src={src} controls autoPlay playsInline
          onError={() => setErr('Could not load the video preview.')}
          style={{ width: '100%', maxHeight: '60vh', borderRadius: 6, background: '#000' }} />
        {err ? <div style={{ color: '#ff9f8a', fontSize: 12 }}>{err}</div> : null}
        <div style={{ fontSize: 11.5, color: '#d1d1d6' }}>
          {coverMs != null ? `Cover frame set at ${fmtMs(coverMs)}.` : 'Pause or seek to the frame you want, then set it as the Reel cover.'}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {coverMs != null ? <button style={modalBtn} onClick={onClearCover}>Reset cover</button> : null}
          <button style={modalBtn} onClick={capture}>Use this frame as cover</button>
          <button style={{ ...modalBtn, background: 'var(--navy)', color: '#fff', border: '1px solid var(--navy)' }} onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid var(--gray-200)', borderRadius: 6, fontFamily: 'Montserrat', color: 'var(--navy)', boxSizing: 'border-box' };
const ghostBtn = { background: '#fff', color: 'var(--navy)', border: '1px solid var(--gray-200)', borderRadius: 6, fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: 'Montserrat', padding: '6px 12px' };
const miniBtn = { background: '#fff', color: 'var(--navy)', border: '1px solid var(--gray-200)', borderRadius: 4, fontWeight: 600, fontSize: 10, cursor: 'pointer', fontFamily: 'Montserrat', padding: '2px 6px', lineHeight: 1.4 };

const BRANDS = [{ slug: 'crabplace', name: 'Crab Place' }, { slug: 'crabandcruise', name: 'Crab & Cruise' }];

// Nearest common aspect-ratio label for a w×h, so the picker shows each photo's
// shape at a glance (instead of everything looking square).
function aspectLabel(w, h) {
  if (!w || !h) return '';
  const r = w / h;
  const known = [[1, '1:1'], [0.8, '4:5'], [1.25, '5:4'], [0.5625, '9:16'], [1.7778, '16:9'], [0.75, '3:4'], [1.3333, '4:3'], [0.6667, '2:3'], [1.5, '3:2']];
  let best = known[0], bd = Infinity;
  known.forEach((k) => { const d = Math.abs(r - k[0]); if (d < bd) { bd = d; best = k; } });
  return best[1];
}

export default function InstagramBuilder() {
  const user = getCurrentUser();
  const [brand, setBrand] = useState('crabplace'); // Crab Place / Crab & Cruise
  const [mediaMode, setMediaMode] = useState('photo'); // 'photo' | 'video'
  const [photos, setPhotos] = useState([]);      // [{ asset, file, preview }]
  const [video, setVideo] = useState(null);      // a video media_assets row
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [cover, setCover] = useState(null);      // { ms, frame? } — chosen Reel cover frame
  const [vidStatus, setVidStatus] = useState(''); // progress text while publishing a Reel
  const [pickerOpen, setPickerOpen] = useState(false);
  const [cropIdx, setCropIdx] = useState(null);
  const [urlInput, setUrlInput] = useState('');   // paste a Media Library "Copy" URL to add an image
  const [dragActive, setDragActive] = useState(false);
  const [dropBusy, setDropBusy] = useState(false);
  const [saveToLibrary, setSaveToLibrary] = useState(true); // save dropped files to the Media Library
  const [caption, setCaption] = useState('');
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  const [posting, setPosting] = useState(false);
  const [postMsg, setPostMsg] = useState(null);  // { ok, text, link }
  const [igPosts, setIgPosts] = useState([]);          // viral / top posts (right column)
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiOptions, setAiOptions] = useState(null);
  const [aiErr, setAiErr] = useState(null);
  const [grammarBusy, setGrammarBusy] = useState(false);
  const [grammarFixed, setGrammarFixed] = useState(null); // corrected text | '__clean__' | null

  async function generateCaption() {
    if (!aiPrompt.trim() || aiLoading) return;
    setAiLoading(true); setAiErr(null); setAiOptions(null);
    const ctx = photos[0] ? `\n\nThe attached image is described as: "${photos[0].asset.alt_text || ''}" (tags: ${(photos[0].asset.tags || []).join(', ')}).` : '';
    try {
      const data = await callClaude({ model: CAPTION_MODEL, max_tokens: 1000, system: IG_SYS, messages: [{ role: 'user', content: `Write Instagram captions for this post idea: ${aiPrompt.trim()}${ctx}` }] });
      const text = (data.content || []).filter((b) => b.type === 'text').map((b) => b.text).join('');
      let opts; try { opts = JSON.parse(text.slice(text.indexOf('['), text.lastIndexOf(']') + 1)); } catch { opts = [text.trim()]; }
      setAiOptions(Array.isArray(opts) ? opts.filter(Boolean) : [String(opts)]);
    } catch (e) { setAiErr(e.message); }
    finally { setAiLoading(false); }
  }

  async function checkGrammar() {
    if (!caption.trim() || grammarBusy) return;
    setGrammarBusy(true); setGrammarFixed(null); setAiErr(null);
    try {
      const data = await callClaude({ model: CAPTION_MODEL, max_tokens: 1000, system: 'You are a careful proofreader for Instagram captions. Fix ONLY spelling, grammar, and punctuation mistakes. Preserve the wording, tone, emojis, hashtags, and line breaks as closely as possible. NEVER use em dashes or en dashes (use periods, commas, or parentheses). Do not rewrite, shorten, or add content. Return ONLY the corrected caption text, nothing else.', messages: [{ role: 'user', content: caption }] });
      const text = (data.content || []).filter((b) => b.type === 'text').map((b) => b.text).join('').trim();
      setGrammarFixed(text && text !== caption.trim() ? text : '__clean__');
    } catch (e) { setAiErr(e.message); }
    finally { setGrammarBusy(false); }
  }

  const loadPosts = useCallback(() => {
    organicFetch({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'ig-viral', brand }) })
      .then(r => r.json()).then(d => setIgPosts(Array.isArray(d.posts) ? d.posts : [])).catch(() => {});
  }, [brand]);
  useEffect(() => { loadPosts(); }, [loadPosts]);

  function addPhotos(assets) {
    const list = Array.isArray(assets) ? assets : [assets];
    setPhotos((prev) => [...prev, ...list.slice(0, MAX_PHOTOS - prev.length).map((a) => ({ asset: a, file: null, preview: null }))]);
  }
  // Add an image from a pasted URL (Media Library "Copy" button, or any public image URL).
  async function addImageUrl(raw) {
    const url = String(raw || '').trim();
    if (photos.length >= MAX_PHOTOS) { setPostMsg({ ok: false, text: `You can attach up to ${MAX_PHOTOS} ${mediaMode === 'story' ? 'frames' : 'photos'}.` }); return; }
    // A pasted HEIC/HEIF URL can't be displayed by Instagram — fetch the bytes and
    // convert to JPEG server-side (via the Media Library), then attach the result.
    if (/\.(heic|heif)(\?|$)/i.test(url)) {
      setPostMsg(null); setDropBusy(true);
      try {
        const blob = await fetch(url).then((r) => { if (!r.ok) throw new Error(`couldn't fetch it (${r.status})`); return r.blob(); });
        const rawBase64 = await new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(String(fr.result).slice(String(fr.result).indexOf(',') + 1)); fr.onerror = () => rej(new Error('could not read the file')); fr.readAsDataURL(blob); });
        const res = await fetch(`${SITE_URL}/.netlify/functions/media-library-upload`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filename: (url.split('/').pop() || 'image.heic').split('?')[0], contentType: 'image/heic', brand: brand === 'crabandcruise' ? 'crab-and-cruise' : 'crab-place', admin_id: user?.id, originalBase64: rawBase64 }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || `upload failed (${res.status})`);
        const asset = data.row || data;
        setUrlInput('');
        setPhotos((prev) => (prev.length >= MAX_PHOTOS ? prev : [...prev, { asset, file: null, preview: null }]));
      } catch (e) {
        setPostMsg({ ok: false, text: `Couldn't add that HEIC URL: ${e.message}. If it's hosted on another site, download it and drag the file in instead.` });
      } finally { setDropBusy(false); }
      return;
    }
    const asset = assetFromUrl(raw);
    if (!asset) { setPostMsg({ ok: false, text: 'That does not look like a valid image URL.' }); return; }
    if (VIDEO_URL_RE.test(asset.r2_original_key || asset.__externalUrl || '')) {
      setPostMsg({ ok: false, text: 'That looks like a video URL. Pick videos from the Media Library so we can process them for Instagram.' });
      return;
    }
    setPostMsg(null);
    setUrlInput('');
    setPhotos((prev) => (prev.length >= MAX_PHOTOS ? prev : [...prev, { asset, file: null, preview: null }]));
  }
  // Drag & drop image files onto the media slot. When "Save to Media Library" is
  // on, each file goes through the same classify/persist pipeline as the Library
  // uploader (so it's reusable) and we attach the returned asset; when off, it's
  // kept local and uploaded just-in-time at publish (used for this post only).
  async function addDroppedFiles(fileList) {
    const files = Array.from(fileList || []).filter((f) => isVideoFile(f) || /^image\//.test(f.type) || /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(f.name));
    if (!files.length) { setPostMsg({ ok: false, text: 'Drop an image (JPG, PNG, WEBP, HEIC) or a video (MP4, MOV).' }); return; }
    const room = Math.max(0, MAX_PHOTOS - photos.length);
    const take = files.slice(0, room);
    if (!take.length) { setPostMsg({ ok: false, text: `You can attach up to ${MAX_PHOTOS} ${mediaMode === 'story' ? 'frames' : 'photos'}.` }); return; }
    setPostMsg(null);
    for (const f of take) {
      if (isVideoFile(f)) {
        // Videos always go to the Media Library: publishing reads the stored copy by its R2 key.
        setDropBusy(true);
        try {
          const asset = await uploadVideoToLibrary(f, { brand: brand === 'crabandcruise' ? 'crab-and-cruise' : 'crab-place', adminId: user?.id });
          setPhotos((prev) => (prev.length >= MAX_PHOTOS ? prev : [...prev, { asset, file: null, preview: null }]));
        } catch (e) { setPostMsg({ ok: false, text: `Could not add "${f.name}": ${e.message}` }); }
        finally { setDropBusy(false); }
      } else if (isHeicFile(f)) {
        // Browser can't decode HEIC — send raw bytes; the server converts to JPEG
        // + makes the thumbnail. Always lands in the Media Library.
        setDropBusy(true);
        try {
          const rawBase64 = await fileToRawBase64(f);
          const res = await fetch(`${SITE_URL}/.netlify/functions/media-library-upload`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ filename: f.name, contentType: 'image/heic', brand: brand === 'crabandcruise' ? 'crab-and-cruise' : 'crab-place', admin_id: user?.id, originalBase64: rawBase64 }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || `Upload failed (${res.status})`);
          const asset = data.row || data;
          setPhotos((prev) => (prev.length >= MAX_PHOTOS ? prev : [...prev, { asset, file: null, preview: null }]));
        } catch (e) { setPostMsg({ ok: false, text: `Could not add "${f.name}": ${e.message}` }); }
        finally { setDropBusy(false); }
      } else if (saveToLibrary) {
        setDropBusy(true);
        try {
          const [orig, thumb] = await Promise.all([imageToBase64(f, 2048, 0.88), imageToBase64(f, 480, 0.78)]);
          const res = await fetch(`${SITE_URL}/.netlify/functions/media-library-upload`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ filename: f.name, contentType: 'image/jpeg', brand: brand === 'crabandcruise' ? 'crab-and-cruise' : 'crab-place', admin_id: user?.id, originalBase64: orig.base64, thumbBase64: thumb.base64, width: orig.naturalWidth, height: orig.naturalHeight }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || `Upload failed (${res.status})`);
          const asset = data.row || data;
          setPhotos((prev) => (prev.length >= MAX_PHOTOS ? prev : [...prev, { asset, file: null, preview: null }]));
        } catch (e) { setPostMsg({ ok: false, text: `Could not save "${f.name}" to the Media Library: ${e.message}` }); }
        finally { setDropBusy(false); }
      } else {
        setDropBusy(true);
        try {
          const jpeg = await fileToJpegFile(f, 1920, 0.9);
          const item = { asset: { __localUrl: URL.createObjectURL(jpeg) }, file: jpeg, preview: null };
          setPhotos((prev) => (prev.length >= MAX_PHOTOS ? prev : [...prev, item]));
        } catch (e) { setPostMsg({ ok: false, text: `Could not read "${f.name}": ${e.message}` }); }
        finally { setDropBusy(false); }
      }
    }
  }
  // Drop a video straight onto the Reel slot (Rebecca, Oct 25, 2026: no Media Library detour first).
  async function addDroppedReel(fileList) {
    const f = Array.from(fileList || []).find(isVideoFile);
    if (!f) { setPostMsg({ ok: false, text: 'Drop a video file (MP4 or MOV) here.' }); return; }
    setPostMsg(null); setDropBusy(true);
    try {
      const asset = await uploadVideoToLibrary(f, { brand: brand === 'crabandcruise' ? 'crab-and-cruise' : 'crab-place', adminId: user?.id });
      setVideo(asset); setCover(null);
    } catch (e) { setPostMsg({ ok: false, text: `Could not add "${f.name}": ${e.message}` }); }
    finally { setDropBusy(false); }
  }
  function removePhoto(i) {
    setPhotos((prev) => { const p = prev[i]; if (p && p.preview) { try { URL.revokeObjectURL(p.preview); } catch (e) { /* noop */ } } return prev.filter((_, idx) => idx !== i); });
  }
  function clearPhotos() {
    setPhotos((prev) => { prev.forEach((p) => { if (p.preview) { try { URL.revokeObjectURL(p.preview); } catch (e) { /* noop */ } } }); return []; });
  }
  function confirmCrop(items) {
    const it = items && items[0]; const i = cropIdx; setCropIdx(null);
    if (i == null || !it || !it.file) return;
    setPhotos((prev) => prev.map((p, idx) => {
      if (idx !== i) return p;
      if (p.preview) { try { URL.revokeObjectURL(p.preview); } catch (e) { /* noop */ } }
      return { ...p, file: it.file, preview: URL.createObjectURL(it.file) };
    }));
  }
  function undoCrop(i) {
    setPhotos((prev) => prev.map((p, idx) => {
      if (idx !== i) return p;
      if (p.preview) { try { URL.revokeObjectURL(p.preview); } catch (e) { /* noop */ } }
      return { ...p, file: null, preview: null };
    }));
  }
  async function uploadCrop(file) {
    const dataUrl = await new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = reject; r.readAsDataURL(file); });
    const base64 = String(dataUrl).split(',')[1];
    const res = await fetch(`${SITE_URL}/.netlify/functions/r2-upload`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ filename: 'ig-crop', contentType: 'image/jpeg', data: base64 }) });
    const d = await res.json();
    if (!res.ok || !d.url) throw new Error(d.error || 'Upload failed');
    return d.url;
  }

  // Download the chosen photos + copy the caption, so it can be posted manually in
  // the IG/FB app (where licensed music + app-only features are available — Meta has
  // no API to push a draft into the app).
  async function downloadForApp() {
    if (!photos.length) { setPostMsg({ ok: false, text: 'Add at least one photo first.' }); return; }
    try {
      if (caption) { try { await navigator.clipboard.writeText(caption); } catch (e) { /* clipboard may be blocked */ } }
      let idx = 0;
      for (const p of photos) {
        idx++;
        const a = document.createElement('a');
        if (p.file) { a.href = URL.createObjectURL(p.file); a.download = `post-${idx}.jpg`; }
        else { const key = p.asset && p.asset.r2_original_key; if (!key) continue; a.href = `/.netlify/functions/r2-fetch?key=${encodeURIComponent(key)}&filename=post-${idx}.jpg`; a.download = `post-${idx}.jpg`; }
        document.body.appendChild(a); a.click(); a.remove();
        if (p.file) setTimeout(() => { try { URL.revokeObjectURL(a.href); } catch (e) { /* noop */ } }, 6000);
        await new Promise((r) => setTimeout(r, 500));
      }
      setPostMsg({ ok: true, text: `${photos.length} image${photos.length > 1 ? 's' : ''} downloaded${caption ? ' and caption copied to your clipboard' : ''}. Open Instagram or Facebook, pick the image(s), add your music, and paste the caption.` });
    } catch (e) { setPostMsg({ ok: false, text: e.message }); }
  }

  async function publish() {
    if (posting) return;
    if (!photos.length) { setPostMsg({ ok: false, text: 'Add at least one photo.' }); return; }
    const isStory = mediaMode === 'story';
    // A lone video isn't a carousel and isn't a Story image — steer to the Reel tab.
    if (!isStory && photos.length === 1 && isVideoAsset(photos[0].asset)) {
      setPostMsg({ ok: false, text: 'Add another item to make a carousel, or use the Reel (video) tab to post a single video.' });
      return;
    }
    setPosting(true); setPostMsg(null);
    try {
      const createdBy = user?.staffName || user?.id || null;
      const scheduledFor = (!isStory && mediaMode === 'photo' && scheduleOn && scheduleAt) ? new Date(scheduleAt).toISOString() : null;
      let reqBody;
      if (isStory) {
        // Each frame posts as its own Story (in order). Photos and videos both work.
        const mediaItems = [];
        for (const p of photos) {
          if (isVideoAsset(p.asset)) mediaItems.push({ type: 'video', videoKey: p.asset.r2_original_key });
          else mediaItems.push({ type: 'image', url: p.file ? await uploadCrop(p.file) : igPublicUrl(p.asset) });
        }
        reqBody = { action: 'ig-publish', brand, mediaType: 'story', mediaItems, caption: '', scheduledFor: null, createdBy };
      } else if (photos.length === 1) {
        // Single photo — keep the existing single-photo path so nothing regresses.
        const p = photos[0];
        const url = p.file ? await uploadCrop(p.file) : igPublicUrl(p.asset);
        reqBody = { action: 'ig-publish', brand, mediaType: 'photo', mediaUrls: [url], caption, scheduledFor, createdBy };
      } else {
        // 2 to 10 items — mixed photo + video carousel.
        const mediaItems = [];
        for (const p of photos) {
          if (isVideoAsset(p.asset)) mediaItems.push({ type: 'video', videoKey: p.asset.r2_original_key });
          else mediaItems.push({ type: 'image', url: p.file ? await uploadCrop(p.file) : igPublicUrl(p.asset) });
        }
        reqBody = { action: 'ig-publish', brand, mediaType: 'carousel', mediaItems, caption, scheduledFor, createdBy };
      }
      let d;
      try {
        const res = await organicFetch({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(reqBody) });
        d = await res.json();
      } catch (e) {
        // A multi-image carousel can take longer than the gateway's response
        // window, so the connection times out even though Instagram usually
        // finishes the post. Don't show a scary error or risk a duplicate.
        setPostMsg({ ok: true, text: 'Instagram is still finishing this post — it can take a minute to appear. Check your Instagram (or the Calendar tab) before publishing again, so you do not post it twice.' });
        clearPhotos(); setCaption(''); setScheduleOn(false); setScheduleAt('');
        setTimeout(() => { loadPosts(); }, 5000);
        setPosting(false);
        return;
      }
      if (d.scheduled) {
        setPostMsg({ ok: true, text: `Scheduled for ${new Date(d.scheduledFor).toLocaleString()}.` });
        clearPhotos(); setCaption(''); setScheduleOn(false); setScheduleAt('');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (d.published) {
        setPostMsg({ ok: true, text: mediaMode === 'story' ? `Posted ${d.count || 1} frame${(d.count || 1) > 1 ? 's' : ''} to your Instagram Story (visible 24 hours).` : 'Published to Instagram.', link: d.permalink });
        clearPhotos(); setCaption('');
        setTimeout(loadPosts, 1500);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (d.needsPermission) {
        setPostMsg({ ok: false, text: d.message || 'The token needs Instagram publishing permission.' });
      } else {
        setPostMsg({ ok: false, text: d.error || 'Could not publish.' });
      }
    } catch (e) { setPostMsg({ ok: false, text: e.message }); }
    finally { setPosting(false); }
  }

  async function publishVideo() {
    if (posting || !video) return;
    setPosting(true); setPostMsg(null);
    const api = (body) => organicFetch({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...body, brand }) }).then((r) => r.json());
    try {
      setVidStatus('Sending the video to Instagram…');
      const start = await api({ action: 'ig-video-start', videoKey: video.r2_original_key, caption, thumbOffsetMs: cover ? cover.ms : null });
      if (start.error || !start.containerId) { setPostMsg({ ok: false, text: start.error || start.message || 'Could not start the upload.' }); return; }
      setVidStatus('Instagram is processing the Reel…');
      let ready = false;
      for (let i = 0; i < 45; i++) {       // up to ~3 min
        await sleep(4000);
        const c = await api({ action: 'ig-video-check', containerId: start.containerId });
        if (c.status === 'FINISHED') { ready = true; break; }
        if (c.status === 'ERROR') { setPostMsg({ ok: false, text: c.error || 'Instagram could not process the video.' }); return; }
      }
      if (!ready) { setPostMsg({ ok: false, text: 'Instagram is taking unusually long — check the app in a few minutes.' }); return; }
      setVidStatus('Publishing…');
      const pub = await api({ action: 'ig-video-publish', containerId: start.containerId, videoKey: video.r2_original_key, caption, createdBy: user?.staffName || user?.id || null });
      if (pub.published) {
        setPostMsg({ ok: true, text: 'Reel published to Instagram.', link: pub.permalink });
        setVideo(null); setCover(null); setCaption('');
        setTimeout(loadPosts, 2000);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else { setPostMsg({ ok: false, text: pub.error || 'Could not publish the Reel.' }); }
    } catch (e) { setPostMsg({ ok: false, text: e.message }); }
    finally { setPosting(false); setVidStatus(''); }
  }

  return (
    <>
      {/* Brand switcher */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, alignItems: 'center' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.3, marginRight: 4 }}>Posting as</span>
        {BRANDS.map((b) => (
          <button key={b.slug} onClick={() => setBrand(b.slug)} disabled={posting}
            style={{ fontSize: 12, fontWeight: 700, padding: '5px 14px', borderRadius: 16, cursor: 'pointer', fontFamily: 'Montserrat', background: brand === b.slug ? 'var(--navy)' : '#fff', color: brand === b.slug ? '#fff' : 'var(--navy)', border: `1px solid ${brand === b.slug ? 'var(--navy)' : 'var(--gray-200)'}` }}>
            {b.name}
          </button>
        ))}
      </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
      <div>
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Create an Instagram post</span>
            <button type="button" onClick={() => { clearPhotos(); setVideo(null); setCover(null); setCaption(''); setScheduleOn(false); setScheduleAt(''); setPostMsg(null); }} style={{ ...ghostBtn, fontSize: 11, padding: '4px 10px' }}>Clear</button>
          </div>
          <div className="card-body" style={{ padding: 16 }}>
            {postMsg && (
              <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, fontSize: 12, background: postMsg.ok ? '#e9f8ef' : '#fdecea', color: postMsg.ok ? '#1a8a4a' : '#c0392b' }}>
                {postMsg.text}{postMsg.ok && postMsg.link && <> <a href={postMsg.link} target="_blank" rel="noreferrer" style={{ color: '#1a8a4a', fontWeight: 700 }}>View on Instagram ↗</a></>}
              </div>
            )}

            {/* Photo / Reel toggle */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
              {[{ k: 'photo', label: 'Photo(s)' }, { k: 'video', label: 'Reel (video)' }, { k: 'story', label: 'Story' }].map((t) => (
                <button key={t.k} onClick={() => setMediaMode(t.k)} disabled={posting}
                  style={{ fontSize: 12, fontWeight: 600, padding: '5px 14px', borderRadius: 16, cursor: 'pointer', fontFamily: 'Montserrat', background: mediaMode === t.k ? 'var(--navy)' : '#fff', color: mediaMode === t.k ? '#fff' : 'var(--navy)', border: `1px solid ${mediaMode === t.k ? 'var(--navy)' : 'var(--gray-200)'}` }}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* Photos */}
            {(mediaMode === 'photo' || mediaMode === 'story') && (
            <div
              onDragOver={(e) => { e.preventDefault(); if (!dragActive) setDragActive(true); }}
              onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
              onDrop={(e) => { e.preventDefault(); setDragActive(false); addDroppedFiles(e.dataTransfer.files); }}
              style={{ marginBottom: 12, padding: dragActive ? 8 : 0, borderRadius: 8, outline: dragActive ? '2px dashed var(--navy)' : 'none', outlineOffset: 2, background: dragActive ? 'rgba(0,59,92,0.05)' : 'transparent', transition: 'background 0.12s, padding 0.12s' }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.3 }}>{mediaMode === 'story' ? 'Story frames' : 'Photos'}{dropBusy ? <span style={{ color: 'var(--navy)', marginLeft: 6, textTransform: 'none', fontWeight: 600 }}>· saving to Library…</span> : null}</label>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start', marginTop: 6 }}>
                {photos.map((p, i) => {
                  const isVid = isVideoAsset(p.asset);
                  return (
                  <div key={i} style={{ width: 84 }}>
                    <div style={{ position: 'relative', width: 84, height: 84 }}>
                      <img src={isVid ? videoPosterUrl(p.asset) : (p.preview || assetUrl(p.asset))} alt="" style={{ width: 84, height: 84, objectFit: 'contain', borderRadius: 6, border: `1px solid ${p.preview ? '#2e9e5b' : 'var(--gray-200)'}`, background: '#f0f0f3', display: 'block' }} />
                      {isVid && (
                        <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: '50%', background: 'rgba(0,0,0,0.45)' }}>
                            <Play size={13} fill="#fff" stroke="#fff" />
                          </span>
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 9.5, color: 'var(--gray-500)', textAlign: 'center', marginTop: 2, lineHeight: 1.15 }}>
                      {isVid ? 'video' : (p.preview ? 'cropped' : (p.asset.width && p.asset.height ? `${p.asset.width}×${p.asset.height} · ${aspectLabel(p.asset.width, p.asset.height)}` : '—'))}
                    </div>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center', marginTop: 3 }}>
                      {!isVid && <button style={miniBtn} onClick={() => setCropIdx(i)}>{p.preview ? '✓ Crop' : 'Crop'}</button>}
                      {!isVid && p.preview && <button style={miniBtn} onClick={() => undoCrop(i)} title="Undo crop">↺</button>}
                      <button style={miniBtn} onClick={() => removePhoto(i)} title="Remove">✕</button>
                    </div>
                  </div>
                  );
                })}
                {photos.length < MAX_PHOTOS && (
                  <button onClick={() => setPickerOpen(true)} style={{ width: 84, height: 84, borderRadius: 6, border: '1px dashed var(--gray-300)', background: '#fafafa', cursor: 'pointer', color: 'var(--gray-500)', fontSize: 11, fontWeight: 600, fontFamily: 'Montserrat', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                    <ImageIcon size={16} />{mediaMode === 'story' ? (photos.length ? '+ Add Another' : 'Pick Content') : (photos.length ? 'Add more' : 'Pick photos')}
                  </button>
                )}
              </div>
              {photos.length < MAX_PHOTOS && (
                <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center', maxWidth: 440 }}>
                  <input
                    type="text"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addImageUrl(urlInput); } }}
                    placeholder="…or paste an image URL (e.g. the Media Library Copy link)"
                    style={{ flex: 1, padding: '6px 9px', fontSize: 11.5, border: '1px solid var(--gray-200)', borderRadius: 5, fontFamily: 'inherit', boxSizing: 'border-box' }} />
                  <button type="button" onClick={() => addImageUrl(urlInput)} disabled={!urlInput.trim()}
                    style={{ fontSize: 11.5, fontWeight: 600, padding: '6px 13px', borderRadius: 5, border: '1px solid var(--navy)', background: urlInput.trim() ? 'var(--navy)' : '#fff', color: urlInput.trim() ? '#fff' : 'var(--gray-400)', cursor: urlInput.trim() ? 'pointer' : 'default', fontFamily: 'Montserrat' }}>
                    Add
                  </button>
                </div>
              )}
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 11.5, color: '#55565a', cursor: 'pointer', userSelect: 'none' }}>
                <input type="checkbox" checked={saveToLibrary} onChange={(e) => setSaveToLibrary(e.target.checked)} />
                Save dropped files to the Media Library
              </label>
              <div style={{ fontSize: 10.5, color: 'var(--gray-400)', marginTop: 3 }}>
                Drag an image onto this area, paste a URL above, or pick from the Library. {saveToLibrary ? 'Dropped files are saved to the Media Library.' : 'Dropped files are used for this post only.'}
              </div>
              <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 6 }}>
                {mediaMode === 'story'
                  ? 'Each item posts as its own Story frame (in order). Photos and videos both work. Instagram posts Stories immediately (no caption or scheduling). Vertical 9:16 looks best.'
                  : <>1 photo = single post. Mix 2 to 10 photos and videos for a carousel. Crop opens at <strong>Portrait 4:5</strong> (most feed space on Instagram); square 1:1 also works. (Videos come from the Media Library picker.)</>}
              </div>
              {(() => {
                const shapes = new Set(photos.filter((p) => !p.preview && p.asset && p.asset.width && p.asset.height).map((p) => aspectLabel(p.asset.width, p.asset.height)));
                return photos.length > 1 && shapes.size > 1 ? (
                  <div style={{ fontSize: 10.5, color: '#9a5b00', background: '#fff8ef', border: '1px solid #ffd9a8', borderRadius: 5, padding: '6px 9px', marginTop: 6 }}>
                    These photos are different shapes. Crop them to the same aspect (e.g. 4:5) so the carousel lines up cleanly.
                  </div>
                ) : null;
              })()}
            </div>
            )}

            {/* Reel (video) */}
            {mediaMode === 'video' && (
            <div
              onDragOver={(e) => { e.preventDefault(); if (!dragActive) setDragActive(true); }}
              onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
              onDrop={(e) => { e.preventDefault(); setDragActive(false); addDroppedReel(e.dataTransfer.files); }}
              style={{ marginBottom: 12, padding: dragActive ? 8 : 0, borderRadius: 8, outline: dragActive ? '2px dashed var(--navy)' : 'none', outlineOffset: 2, background: dragActive ? 'rgba(0,59,92,0.05)' : 'transparent', transition: 'background 0.12s, padding 0.12s' }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.3 }}>Reel video{dropBusy ? <span style={{ color: 'var(--navy)', marginLeft: 6, textTransform: 'none', fontWeight: 600 }}>· uploading to Library…</span> : null}</label>
              <div style={{ marginTop: 6 }}>
                {video ? (
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <button type="button" onClick={() => setVideoModalOpen(true)} title="Preview video"
                      style={{ position: 'relative', width: 64, height: 84, padding: 0, borderRadius: 6, border: '1px solid var(--gray-200)', background: '#000', cursor: 'pointer', overflow: 'hidden', flex: '0 0 auto' }}>
                      {cover ? (
                        <SeekedFrame src={videoSrcUrl(video)} ms={cover.ms} />
                      ) : (
                        <img src={videoPosterUrl(video)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      )}
                      <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.22)' }}>
                        <Play size={18} fill="#fff" stroke="#fff" />
                      </span>
                    </button>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>{video.original_name || video.suggested_name || 'Selected video'}</div>
                      {cover ? <div style={{ fontSize: 10.5, color: '#1a8a4a', marginTop: 2 }}>Cover frame at {fmtMs(cover.ms)}</div> : null}
                      <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                        <button style={miniBtn} onClick={() => setVideoModalOpen(true)}>Preview / cover</button>
                        <button style={miniBtn} onClick={() => setPickerOpen(true)}>Replace</button>
                        <button style={miniBtn} onClick={() => { setVideo(null); setCover(null); }}>Remove</button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setPickerOpen(true)} style={{ width: 120, height: 84, borderRadius: 6, border: '1px dashed var(--gray-300)', background: '#fafafa', cursor: 'pointer', color: 'var(--gray-500)', fontSize: 11, fontWeight: 600, fontFamily: 'Montserrat', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                    <Film size={16} />Pick or drop a video
                  </button>
                )}
              </div>
              <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 6 }}>
                Posts as an Instagram <strong>Reel</strong> (also shows in your feed). Vertical 9:16 video works best. Instagram processing can take up to a minute.
              </div>
            </div>
            )}

            {/* Caption */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.3 }}>Caption</label>
                <MicButton value={caption} onChange={setCaption} />
              </div>
              <textarea rows={5} value={caption} onChange={(e) => setCaption(e.target.value)} spellCheck="true" placeholder="Write the Instagram caption. Add hashtags. (Links in IG captions aren't clickable — use 'link in bio'.)" style={{ ...inputStyle, resize: 'vertical', marginTop: 4 }} />
              {caption.trim() && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                  <button type="button" onClick={checkGrammar} disabled={grammarBusy}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: '#7c3aed', background: '#faf7ff', border: '1px solid #e7dbff', borderRadius: 5, padding: '4px 9px', cursor: grammarBusy ? 'default' : 'pointer', fontFamily: 'Montserrat' }}>
                    {grammarBusy ? <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <Sparkles size={11} />} Check spelling &amp; grammar
                  </button>
                  {grammarFixed === '__clean__' && <span style={{ fontSize: 11, color: '#1a8a4a' }}>✓ Looks good, no changes.</span>}
                </div>
              )}
              {grammarFixed && grammarFixed !== '__clean__' && (
                <div style={{ marginTop: 6, padding: '8px 10px', background: '#f7f5ff', border: '1px solid #e7dbff', borderRadius: 6 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 }}>Suggested fix</div>
                  <div style={{ fontSize: 12.5, color: 'var(--navy)', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>{grammarFixed}</div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 7 }}>
                    <button type="button" onClick={() => { setCaption(grammarFixed); setGrammarFixed(null); }} style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: '#7c3aed', border: 'none', borderRadius: 5, padding: '5px 11px', cursor: 'pointer', fontFamily: 'Montserrat' }}>Use this</button>
                    <button type="button" onClick={() => setGrammarFixed(null)} style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 5, padding: '5px 11px', cursor: 'pointer', fontFamily: 'Montserrat' }}>Keep mine</button>
                  </div>
                </div>
              )}
            </div>

            {/* AI caption assistant */}
            <div style={{ background: '#faf7ff', border: '1px solid #e7dbff', borderRadius: 6, padding: 10, marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', marginBottom: 6 }}><Sparkles size={12} style={{ verticalAlign: 'middle' }} /> AI caption assistant</div>
              <textarea rows={2} value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} placeholder="Describe the post (e.g. happy customers at a crab feast, hero the steamed blue crabs)…" style={{ ...inputStyle, resize: 'vertical', marginBottom: 6 }} />
              <button style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: '#7c3aed', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontFamily: 'Montserrat' }} onClick={generateCaption} disabled={aiLoading || !aiPrompt.trim()}>
                {aiLoading ? <><Loader2 size={12} style={{ animation: 'spin 1s linear infinite', verticalAlign: 'middle', marginRight: 4 }} />Writing…</> : 'Generate 3 captions'}
              </button>
              {aiErr && <div style={{ fontSize: 10.5, color: '#c33', marginTop: 6 }}>{aiErr}</div>}
              {aiOptions && aiOptions.map((opt, i) => (
                <div key={i} style={{ marginTop: 6, padding: 8, background: '#fff', border: '1px solid #ece3ff', borderRadius: 6 }}>
                  <div style={{ fontSize: 12, color: 'var(--navy)', whiteSpace: 'pre-wrap' }}>{opt}</div>
                  <button style={{ marginTop: 4, fontSize: 10.5, fontWeight: 600, color: '#7c3aed', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }} onClick={() => { setCaption(opt); setAiOptions(null); }}>Click to use</button>
                </div>
              ))}
            </div>

            {mediaMode === 'photo' && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, color: 'var(--navy)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={scheduleOn} onChange={(e) => setScheduleOn(e.target.checked)} /> Schedule for later
                </label>
                {scheduleOn && (
                  <input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} style={{ ...inputStyle, marginTop: 6, width: 'auto' }} />
                )}
              </div>
            )}

            {(() => {
              const disabled = posting || (mediaMode === 'video' ? !video : (!photos.length || (mediaMode === 'photo' && scheduleOn && !scheduleAt)));
              return (
                <button onClick={mediaMode === 'video' ? publishVideo : publish} disabled={disabled}
                  style={{ width: '100%', padding: '12px', fontSize: 14, fontWeight: 700, fontFamily: 'Montserrat', border: 'none', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer', color: '#fff', background: disabled ? 'var(--gray-300)' : 'linear-gradient(90deg,#E1306C,#F77737)' }}>
                  {posting ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite', verticalAlign: 'middle', marginRight: 6 }} />{vidStatus || 'Publishing…'}</> : (mediaMode === 'video' ? 'Publish Reel to Instagram' : (mediaMode === 'story' ? 'Post to your Story' : (scheduleOn ? 'Schedule Instagram post' : 'Publish to Instagram')))}
                </button>
              );
            })()}
            {mediaMode !== 'video' && photos.length > 0 && (
              <button type="button" onClick={downloadForApp}
                style={{ width: '100%', marginTop: 8, padding: '9px', fontSize: 12.5, fontWeight: 600, fontFamily: 'Montserrat', borderRadius: 8, cursor: 'pointer', color: 'var(--navy)', background: '#fff', border: '1px solid var(--gray-200)' }}>
                ⬇ Download + copy caption (to post manually with music)
              </button>
            )}
            <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 8, textAlign: 'center' }}>
              Posts to <strong>@thecrabplace</strong>. Instagram posts track <strong>engagement</strong> (likes + comments), not orders.
            </div>
          </div>
        </div>
      </div>

      {/* Top IG posts (viral library) */}
      <div>
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Top Instagram posts <span style={{ fontSize: 10.5, fontWeight: 500, color: 'var(--gray-500)' }}>· by engagement</span></span>
            <button style={{ ...ghostBtn, fontSize: 11, padding: '4px 10px' }} onClick={loadPosts}>Refresh</button>
          </div>
          <div className="card-body" style={{ padding: igPosts.length ? 0 : 16 }}>
            {igPosts.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>No Instagram posts found — the account's most-engaged posts (likes + comments) rank here, like the viral video library on Facebook.</div>
            ) : igPosts.map((p) => (
              <div key={p.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid var(--gray-100)' }}>
                {p.thumb ? <img src={p.thumb} alt="" style={{ width: 44, height: 44, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} /> : <div style={{ width: 44, height: 44, borderRadius: 6, background: 'var(--gray-100)', flexShrink: 0 }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11.5, color: 'var(--navy)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{p.caption || '(no caption)'}</div>
                  <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 2 }}>
                    {p.views ? <>▶ {Number(p.views).toLocaleString()} views · </> : null}♥ {(p.likes || 0).toLocaleString()} · 💬 {(p.comments || 0).toLocaleString()}
                    {p.permalink && <> · <a href={p.permalink} target="_blank" rel="noreferrer" style={{ color: 'var(--orange)', fontWeight: 600 }}>View <ExternalLink size={9} style={{ verticalAlign: 'middle' }} /></a></>}
                  </div>
                </div>
                <button title="Load this caption into the composer to re-post (pick your video)"
                  onClick={() => { setCaption(p.caption || ''); setMediaMode('video'); setPostMsg({ ok: true, text: 'Caption loaded — pick your video below and publish. (Instagram won\'t let apps pull the original posted file, so choose the video from your Media Library.)' }); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  style={{ ...ghostBtn, fontSize: 10, fontWeight: 700, padding: '4px 8px', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <Repeat2 size={11} />Repurpose
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {pickerOpen && (mediaMode === 'photo' || mediaMode === 'story') && (
        <MediaPicker
          multiSelect
          onClose={() => setPickerOpen(false)}
          onPick={(assets) => {
            const arr = Array.isArray(assets) ? assets : (assets ? [assets] : []);
            addPhotos(arr);
            setPickerOpen(false);
          }}
        />
      )}
      {pickerOpen && mediaMode === 'video' && (
        <MediaPicker videosOnly onClose={() => setPickerOpen(false)} onPick={(a) => { const asset = Array.isArray(a) ? a[0] : a; if (asset) { setVideo(asset); setCover(null); } setPickerOpen(false); }} />
      )}
      {videoModalOpen && video && (
        <VideoPreviewModal
          src={videoSrcUrl(video)}
          coverMs={cover ? cover.ms : null}
          onSetCover={(ms) => setCover({ ms })}
          onClearCover={() => setCover(null)}
          onClose={() => setVideoModalOpen(false)}
        />
      )}
      {cropIdx != null && photos[cropIdx] && (
        <ImageCropModal
          images={[{ url: cropSourceUrl(photos[cropIdx].asset), name: 'ig-crop.jpg', alt: photos[cropIdx].asset.alt_text || '' }]}
          initialAspect={mediaMode === 'story' ? '9:16' : '4:5'}
          allowAspectChange
          onCancel={() => setCropIdx(null)}
          onConfirm={confirmCrop}
        />
      )}
    </div>
    </>
  );
}
